/**
 * The inline rate editor (RP-6.2, RP-6.3) — opened by clicking a line's rate in the grid.
 *
 * A popover anchored to the Rate cell rather than the Plan Line dialog, so the row being
 * priced stays in view. It is the same PATCH the dialog sends, so the server's RP-6.1
 * resolution still decides what the rate IS: a figure equal to the list rate is that rate,
 * anything else is a line override and needs a reason (Client-Dictated plans excepted —
 * the contract is its own reason).
 *
 * The reason field shows when the typed rate differs from the line's list price. For a line
 * still on its list price (Card or Account-negotiated) that price is the line's own rate.
 * For a line already overridden, the client only knows the STANDARD card rate — an Account
 * rate is not on the view — so the server stays the backstop and its 422 surfaces as a toast.
 */

import { useState } from "react";
import { describeRateSource } from "@/components/plans/rate-source";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { useUpdatePlanLine } from "@/hooks/use-resource-plans";
import { formatPlanMoney } from "@/lib/plan-format";
import type { PlanLineView, ResourcePlanDetailView } from "@/schemas/resource-plans";

const DECIMAL2 = /^\d+(\.\d{1,2})?$/;
const TYPABLE = /^\d*\.?\d{0,2}$/;

/** "120" and "120.00" are the same rate. */
function sameRate(a: string, b: string): boolean {
  return Number(a) === Number(b);
}

export function PlanLineRateEditor(props: {
  plan: ResourcePlanDetailView;
  line: PlanLineView;
  /** The Rate cell that opened this. */
  anchor: HTMLElement;
  onClose: () => void;
}) {
  const { plan, line } = props;
  const update = useUpdatePlanLine();
  const clientDictated = plan.rateMode === "ClientDictated";
  const overridden = line.rateSource === "LineOverride";
  const source = describeRateSource(line, plan);

  const [rate, setRate] = useState(line.billRate);
  const [reason, setReason] = useState(line.rateOverrideReason ?? "");

  // What a rate is measured against to count as an override (see the header).
  const listPrice = overridden ? line.listRate : line.billRate;
  const needsReason =
    !clientDictated && DECIMAL2.test(rate) && listPrice !== null
      ? !sameRate(rate, listPrice)
      : false;
  const unchanged = DECIMAL2.test(rate) && sameRate(rate, line.billRate);
  const canSave =
    DECIMAL2.test(rate) &&
    !unchanged &&
    (!needsReason || reason.trim() !== "") &&
    !update.isPending;

  function save() {
    if (!canSave) return;
    update.mutate(
      {
        id: line.id,
        billRate: rate,
        rateOverrideReason: needsReason ? reason.trim() : null,
      },
      { onSuccess: props.onClose },
    );
  }

  function resetToList() {
    update.mutate({ id: line.id, resetRateToCard: true }, { onSuccess: props.onClose });
  }

  return (
    <Popover open onOpenChange={(open) => !open && props.onClose()}>
      <PopoverAnchor virtualRef={{ current: props.anchor }} />
      <PopoverContent align="end" className="w-72 gap-3 p-3">
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold">Bill Rate</p>
            <p className="truncate text-xs text-muted-foreground">
              {line.clientLaborCategory ?? line.laborCategoryName}
            </p>
          </div>

          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              $
            </span>
            <Input
              autoFocus
              onFocus={(event) => event.currentTarget.select()}
              inputMode="decimal"
              aria-label="Bill Rate"
              value={rate}
              onChange={(event) => {
                const v = event.target.value;
                if (TYPABLE.test(v)) setRate(v);
              }}
              className="pl-6 tabular-nums"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            {clientDictated
              ? "Client-Dictated rates — every line carries its own."
              : line.listRate !== null
                ? `From the Rate Card on ${plan.rateBasisLabel}: ${formatPlanMoney(line.listRate)}. ${source.departsFromStandard ? source.detail : "This line uses it."}`
                : `No Rate Card rate for this Labor Category on ${plan.rateBasisLabel}.`}
          </p>

          {needsReason && (
            <div className="flex flex-col gap-1">
              <label htmlFor="rate-override-reason" className="text-xs font-medium">
                Override reason
              </label>
              <Input
                id="rate-override-reason"
                placeholder="Negotiated concession, RFP schedule"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">
                Required — it shows in the plan history.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            {!clientDictated && overridden ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={update.isPending}
                onClick={resetToList}
              >
                Reset to Rate Card
              </Button>
            ) : (
              <span />
            )}
            <span className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={props.onClose}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!canSave}>
                Save
              </Button>
            </span>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
